import { useRequiredContext } from "@redotech/react-util/context";
import CatalogIcon from "@redotech/redo-icons/catalog.svg";
import CatalogIconFilled from "@redotech/redo-icons/catalog_filled.svg";
import MessageIcon from "@redotech/redo-icons/message-text-square-02.svg";
import SettingsIcon from "@redotech/redo-icons/settings-02.svg";
import TargetIcon from "@redotech/redo-icons/target-01.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { isAeoAllowed } from "@redotech/redo-model/aeo/is-aeo-allowed";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext } from "react";
import { useLocation, useMatch } from "react-router-dom";
import { AeoCatalogProductsItem } from "./aeo-catalog-products";

export function useAeoNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useContext(TeamContext);

  if (!isAeoAllowed(team?.settings.aeo)) {
    return null;
  }

  const primaryTab: LinkPrimaryTab = {
    id: "aeo",
    hasNotification: false,
    UnfilledIcon: TargetIcon,
    FilledIcon: TargetIcon,
    type: "link",
    href: routes.aeoDashboardHref,
    pathRoot: routes.aeoBaseUrl,
    friendlyName: "AEO",
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "aeo",
    name: "AEO",
    NavContent: SecondaryAeoNavigation,
  };

  return { primaryTab, secondaryNavigationData };
}

const SecondaryAeoNavigation = memo(function SecondaryAeoNavigation() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const location = useLocation();
  const configureChildSelected = useMatch(`${routes.aeoConfigureHref}/*`);

  const isCatalogEnabled = team.settings.ims?.catalogEnabled ?? false;

  const isCatalogChildSelected =
    location.pathname.includes("/aeo/catalog/") ||
    location.pathname.endsWith("/aeo/catalog");

  const { id: nodeId, TreeContext } = useNavTreeNode({ friendlyName: "AEO" });

  return (
    <TreeContext.Provider value={nodeId}>
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.aeoDashboardHref }}
        FilledIcon={TargetIcon}
        friendlyName="Dashboard"
        id="aeo-dashboard"
        UnfilledIcon={TargetIcon}
      />
      <RedoSecondaryNavItem
        action={{ type: "button" }}
        anyChildSelected={isCatalogChildSelected}
        FilledIcon={CatalogIconFilled}
        friendlyName="Catalog"
        hidden={!isCatalogEnabled}
        id="aeo-catalog"
        isFolder
        UnfilledIcon={CatalogIcon}
      >
        <AeoCatalogProductsItem />
      </RedoSecondaryNavItem>
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.aeoConfigureHref }}
        anyChildSelected={!!configureChildSelected}
        FilledIcon={SettingsIcon}
        friendlyName="Configure"
        id="aeo-configure"
        UnfilledIcon={SettingsIcon}
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.aeoPromptsHref }}
        FilledIcon={MessageIcon}
        friendlyName="Prompts"
        id="aeo-prompts"
        UnfilledIcon={MessageIcon}
      />
    </TreeContext.Provider>
  );
});
